import {
  Box, Button, Checkbox, EditPanel, Formblock, FormSection, generateTableFromSchema, PageHeader, Picto, TableSkeleton, Textfield, useTable,
} from '@zolteam/zol-admin-ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useGetAirportsInfiniteQuery } from '../../services/airports/airportsQueries';
import AirportDetails from '../../components/organisms/airportDetails/AirportDetails';

/**
 * AirportList component.
 * @returns {JSX.Element}
 */
export default function AirportList() {
  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  // json schema
  const UserListSchema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Customer',
    properties: {},
    additionalProperties: false,
  };

  // -- search-params
  const [searchParam, setSearchParams] = useSearchParams();
  const sort = searchParam.get('sort') || 'codeSort';
  const order = searchParam.get('order') || 'desc';

  const pageSize = 25;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // local state
  const [currentAirport, setCurrentAirport] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // hooks
  const {
    handleColumnSort,
    dispatch: useTableDispatch,
    selectedRows,
  } = useTable({
    setParams:
    /**
     * Set the search params.
     * @param {object} params
     * @returns {void}
     */
    (params) => setSearchParams((searchParams) => {
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      return searchParams;
    }),
    order,
    sort,
    // @ts-ignore
    defaultSelectedRows: [],
  });

  // Services
  // @ts-ignore - pass dynamic filters to API (server accepts nameFilter/cityFilter/codeFilter)
  const {
    data: dataAirportsList,
    isLoading: isLoadingDataAirportsList,
    refetch,
  } = useGetAirportsInfiniteQuery({
    queryParams: {
      pageSize,
      search: debouncedSearch,
    },
  });

  // Handlers
  /**
   * Handle the click on the new content button.
   * @returns {void}
   */
  const handleNewContentClick = () => navigate('/airports/new');

  /**
   * Handle the click on a table row.
   * @param {any} row
   * @returns {void}
   */
  const handleRowNavigate = (row) => {
    setCurrentAirport(row);
  };

  /**
   * Check if a row is checked
   * @param {string} id
   * @returns {boolean}
   */
  const isRowChecked = (id) => Object.keys(selectedRows).includes(id);

  /**
   * Handle row selection
   * @param {Airport} row
   */
  const handleSelectRow = (row) => {
    if (isRowChecked(row.id?.toString())) {
      useTableDispatch({ type: 'DESELECT_ROW', payload: { id: row.id, singleSelection: false } });
    } else {
      useTableDispatch({ type: 'SELECT_ROW', payload: { id: row.id, singleSelection: false } });
    }
  };

  /**
   * Handle the close of the panel.
   * @returns {void}
   */
  const handleCloseDetailPanel = () => {
    setCurrentAirport(null);
    refetch();
  };

  const airports = useMemo(() => dataAirportsList?.pages?.reduce((
    /** @type {{data: Airport[]}} */acc,
    /** @type {{data: Airport[]}} */page,
  ) => ({
    ...acc,
    data: [...(acc.data || []), ...page.data],
  }), { data: [], total: 0 }), [dataAirportsList]);

  return (
    <div>
      <Box direction="column" gap="s" className="padding-l">
        <PageHeader
          title={(
            <>
              <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
                {t('airportList.title')}
              </h1>
              {airports?.data?.length && airports?.total ? (
                <span className="font-color-neutral-500">
                  {t('airportList.nbRecords', { count: airports.total })}
                </span>
              ) : null}
            </>
        )}
          actions={(
            <Button icon="add" onClick={handleNewContentClick}>
              {t('airportList.actions.new')}
            </Button>
        )}
        />

        {/* airport detail view */}
        <EditPanel
          id="airportDetail"
          onClose={handleCloseDetailPanel}
          isOpen={!!currentAirport}
        >
          {currentAirport
            ? (
              <AirportDetails
                airportId={currentAirport.id}
                handleClosePanel={handleCloseDetailPanel}
              />
            ) : null}
        </EditPanel>

        <FormSection label={t('airportList.filters.title')}>
          <div className="d-flex f-row f-wrap gap-s">
            {/* Filters */}
            <Formblock
              key="name"
              htmlFor="name"
              className="margin-bottom-xs"
            >
              <label htmlFor="name" className="p2-default">
                <span>{t('airportList.filters.name')}</span>
                <Textfield
                  // @ts-ignore
                  id="name"
                  name="name"
                  value={search || ''}
                  onChange={(/** @type {{ target: { value: any; }; }} */ e) => setSearch(e.target.value)}
                />
              </label>
            </Formblock>
          </div>
        </FormSection>

        {isLoadingDataAirportsList
          ? <TableSkeleton />
          : generateTableFromSchema(
            {
              JSONSchema: UserListSchema,
              data: airports?.data || [],
              sort: {
                order,
                active: sort,
                handleColumnSort,
              },
              navigate: {
              // @ts-ignore
                handleRowNavigate,
              },
              labelNoResult: t('bookingList.noResult'),
              customColumns: [
                {
                  key: 'selection',
                  description: '',
                  sortable: false,
                  position: 'start',
                  renderHead: () => <Picto icon="gallery-add" />,
                  renderCell: (/** @type {Airport} */row) => (
                    <div className="cell">
                      <Checkbox
                        id={row.id?.toString()}
                        checked={isRowChecked(row.id?.toString())}
                        size="small"
                    // @ts-ignore
                        onChange={() => handleSelectRow(row)}
                      />
                    </div>
                  ),
                },
                {
                  key: 'code',
                  description: '',
                  sortable: false,
                  position: 'end',
                  renderHead: () => t('airportList.columns.code'),
                  renderCell: (/** @type {Airport} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      {row.code}
                    </button>
                  ),
                },
                {
                  key: 'name',
                  description: '',
                  sortable: false,
                  position: 'end',
                  renderHead: () => t('airportList.columns.name'),
                  renderCell: (/** @type {Airport} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      {row.name}
                    </button>
                  ),
                },
                {
                  key: 'city',
                  description: '',
                  sortable: false,
                  position: 'end',
                  renderHead: () => t('airportList.columns.city'),
                  renderCell: (/** @type {Airport} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      {row.city}
                    </button>
                  ),
                },
              ],
            },
          )}

        {/* {countSelectedRows ? (
          <Box align="flex-end" justify="space-between" fullWidth>
            {t('bookingList.selection.nbBookingSelected', { count: countSelectedRows })}
            <Button
              color="neutral00"
              icon="add-bold"
              onClick={toggleAddDocPanel}
            >
              {t('bookingList.actions.addDocument')}
            </Button>
          </Box>
        ) : null} */}

        {/* {airports?.length && airports?.total ? (
          <Pagination
            recordsCount={airports.total}
            pageNumber={+pageNumber}
            pageSize={+pageSize}
            onPageNumberChange={handlePaginationChange}
            perPageResultLabel={t('pagination.resultPerPage')}
          />
        ) : null} */}
      </Box>
    </div>
  );
}
